import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Table, Switch, Tooltip, Button, Space, Tag, Modal, Popconfirm, Empty, Spin, Typography, message } from 'antd';
import { LockOutlined, MailOutlined, SendOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import notificationService from '../../services/notificationService';
import { useTranslation } from '../../../shared/i18n';

const { Text } = Typography;

/** Telegram 图标（antd 没有） */
const TgIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
        <path d="M9.04 15.6l-.39 5.5c.56 0 .8-.24 1.1-.53l2.63-2.52 5.46 4c1 .55 1.71.26 1.98-.92l3.59-16.8c.33-1.5-.54-2.08-1.5-1.72L1.4 9.6c-1.45.56-1.43 1.37-.25 1.73l5.39 1.68L19.05 5.1c.59-.39 1.13-.17.69.22" />
    </svg>
);

/**
 * 通知设置：渠道（邮箱 / Telegram 绑定）+ 分类 × 渠道开关矩阵。
 * 重要分类锁定开启，后端也会拒绝关闭。
 */
export default function NotificationSettings() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState({});
    const [bind, setBind] = useState(null); // {code, deep_link, expires_at, status}
    const [testing, setTesting] = useState(null);
    const pollRef = useRef(null);

    const load = useCallback(async () => {
        try {
            setData(await notificationService.overview());
            setError(null);
        } catch (e) {
            setError(e.error || e.message || 'error');
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => () => clearInterval(pollRef.current), []);

    const toggle = async (category, channel, enabled) => {
        const key = `${category}.${channel}`;
        setSaving((s) => ({ ...s, [key]: true }));
        setData((d) => ({ ...d, prefs: { ...d.prefs, [category]: { ...d.prefs[category], [channel]: enabled } } }));
        try {
            const r = await notificationService.savePrefs([{ category, channel, enabled }]);
            setData((d) => ({ ...d, prefs: r.prefs }));
        } catch (e) {
            message.error(e.error || t('Failed to save'));
            load();
        } finally {
            setSaving((s) => ({ ...s, [key]: false }));
        }
    };

    const startBind = async () => {
        try {
            const r = await notificationService.telegramBindCode();
            setBind({ ...r, status: 'pending' });
            clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const s = await notificationService.telegramStatus(r.code);
                    if (s.status !== 'pending') {
                        clearInterval(pollRef.current);
                        setBind((b) => (b ? { ...b, status: s.status } : b));
                        if (s.status === 'bound') {
                            message.success(t('Telegram linked'));
                            setBind(null);
                            load();
                        }
                    }
                } catch (_) { /* 网络抖动，下一轮再试 */ }
            }, 2500);
        } catch (e) {
            message.error(e.error || t('Couldn\'t create a link code'));
        }
    };

    const closeBind = () => {
        clearInterval(pollRef.current);
        setBind(null);
    };

    const sendTest = async (channel, channelId) => {
        setTesting(channelId || channel);
        try {
            await notificationService.test(channel, channelId);
            message.success(t('Test message sent'));
        } catch (e) {
            message.error(e.error || t('Send failed'));
        } finally {
            setTesting(null);
        }
    };

    const unbind = async (id) => {
        try {
            await notificationService.removeChannel(id);
            load();
        } catch (e) {
            message.error(e.error || t('Operation failed'));
        }
    };

    if (error) {
        return <Card><Empty description={t('The notification center isn\'t available yet')} /></Card>;
    }
    if (!data) {
        return <Card><div style={{ textAlign: 'center', padding: 40 }}><Spin /></div></Card>;
    }

    const telegrams = (data.channels || []).filter((c) => c.channel === 'telegram');
    const tgOk = !!data.telegram?.enabled;

    const switchCell = (channel) => (_, row) => {
        if (row.mandatory) {
            return (
                <Tooltip title={t('Important notices are always delivered')}>
                    <Space size={4}><Switch size="small" checked disabled /><LockOutlined style={{ color: 'var(--text-tertiary, #999)' }} /></Space>
                </Tooltip>
            );
        }
        return (
            <Switch
                size="small"
                checked={!!data.prefs?.[row.key]?.[channel]}
                loading={!!saving[`${row.key}.${channel}`]}
                onChange={(v) => toggle(row.key, channel, v)}
            />
        );
    };

    const columns = [
        {
            title: t('Category'),
            dataIndex: 'label',
            render: (label, row) => (
                <Space size={4}>
                    <span>{t(label)}</span>
                    <Tooltip title={t(row.desc)}><InfoCircleOutlined style={{ color: 'var(--text-tertiary, #999)', fontSize: 12 }} /></Tooltip>
                </Space>
            ),
        },
        { title: t('Inbox'), width: 90, render: () => <Switch size="small" checked disabled /> },
        { title: t('Email'), width: 110, render: switchCell('email') },
        { title: 'Telegram', width: 110, render: switchCell('telegram') },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card size="small" title={t('Channels')}>
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                    <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Space><MailOutlined /><Text>{data.email}</Text><Tag color="green">{t('Account email')}</Tag></Space>
                        <Button size="small" icon={<SendOutlined />} loading={testing === 'email'} onClick={() => sendTest('email')}>{t('Test')}</Button>
                    </Space>
                    {telegrams.map((c) => (
                        <Space key={c.id} wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                            <Space>
                                <TgIcon />
                                <Text>{c.label || c.target}</Text>
                                {c.disabled ? <Tooltip title={t('The bot was blocked; please link again')}><Tag color="red">{t('Inactive')}</Tag></Tooltip> : <Tag color="blue">Telegram</Tag>}
                            </Space>
                            <Space>
                                {!c.disabled && <Button size="small" icon={<SendOutlined />} loading={testing === c.id} onClick={() => sendTest('telegram', c.id)}>{t('Test')}</Button>}
                                <Popconfirm title={t('Unlink?')} onConfirm={() => unbind(c.id)}>
                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </Space>
                        </Space>
                    ))}
                    <Tooltip title={tgOk ? '' : t('Telegram notifications aren\'t available yet')}>
                        <Button icon={<TgIcon />} onClick={startBind} disabled={!tgOk}>{t('Link Telegram')}</Button>
                    </Tooltip>
                </Space>
            </Card>

            <Card size="small" title={t('What to receive')}>
                <Table
                    rowKey="key"
                    size="small"
                    pagination={false}
                    columns={columns}
                    dataSource={data.categories || []}
                    scroll={{ x: 460 }}
                />
            </Card>

            <Modal open={!!bind} onCancel={closeBind} footer={null} title={t('Link Telegram')} width={340} destroyOnClose>
                {bind && (
                    <div style={{ textAlign: 'center' }}>
                        {bind.status === 'expired' ? (
                            <Space direction="vertical">
                                <Text type="secondary">{t('Link code expired')}</Text>
                                <Button type="primary" onClick={startBind}>{t('Regenerate')}</Button>
                            </Space>
                        ) : (
                            <Space direction="vertical" size={14}>
                                <div style={{ background: '#fff', padding: 10, display: 'inline-block', borderRadius: 8 }}>
                                    <QRCodeSVG value={bind.deep_link} size={180} />
                                </div>
                                <Button type="primary" href={bind.deep_link} target="_blank" rel="noopener noreferrer" icon={<TgIcon />}>
                                    {t('Open Telegram')}
                                </Button>
                                <Text type="secondary" style={{ fontSize: 12 }}><Spin size="small" /> {t('Tap Start in Telegram and it completes automatically')}</Text>
                            </Space>
                        )}
                    </div>
                )}
            </Modal>
        </Space>
    );
}
